/**
 * US Data Subject Request (DSR) Portal Dashboard (US-159 / T-159-002)
 *
 * Full lifecycle privacy rights portal under CCPA / CPRA statutory rules:
 * - Intake of Right to Know / Access, Delete, and Opt-Out consumer requests
 * - 45-day statutory deadline tracking with countdowns & extension triggers
 * - Identity verification, extension, and fulfillment workflows
 * - Full audit trail logging
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  dsrService,
  DSRequest,
  DSRType,
  DSRStatus,
  formatDeadlineCountdown,
  calculateDaysRemaining,
  isOverdue,
} from './dsrService';

type FilterTab = 'ALL' | 'PENDING' | 'IN_PROGRESS' | 'FULFILLED';

export const DsrPortal: React.FC = () => {
  const [requests, setRequests] = useState<DSRequest[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('ALL');
  const [selectedRequest, setSelectedRequest] = useState<DSRequest | null>(null);

  // New DSR Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [newType, setNewType] = useState<DSRType>('ACCESS');
  const [newName, setNewName] = useState<string>('');
  const [newEmail, setNewEmail] = useState<string>('');
  const [newDetails, setNewDetails] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Action status message
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  // Load requests
  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dsrService.listRequests();
      setRequests(data);
      if (data.length > 0 && !selectedRequest) {
        setSelectedRequest(data[0]);
      } else if (selectedRequest) {
        // Refresh selected request reference if it exists
        const updated = data.find((r) => r.id === selectedRequest.id);
        if (updated) setSelectedRequest(updated);
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to load DSR requests'
      );
    } finally {
      setLoading(false);
    }
  }, [selectedRequest]);

  useEffect(() => {
    loadRequests();
  }, []);

  // Filter requests according to tab
  const filteredRequests = requests.filter((req) => {
    switch (activeTab) {
      case 'PENDING':
        return req.status === 'PENDING';
      case 'IN_PROGRESS':
        return (
          req.status === 'IN_PROGRESS' ||
          req.status === 'VERIFIED' ||
          req.status === 'EXTENDED'
        );
      case 'FULFILLED':
        return req.status === 'FULFILLED';
      case 'ALL':
      default:
        return true;
    }
  });

  // Handle New DSR Intake Submission
  const handleCreateDSR = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newEmail.trim() || !newDetails.trim()) {
      setFormError('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      const created = await dsrService.createRequest({
        type: newType,
        subjectName: newName.trim(),
        subjectEmail: newEmail.trim(),
        details: newDetails.trim(),
      });

      // Reset form
      setNewName('');
      setNewEmail('');
      setNewDetails('');
      setNewType('ACCESS');
      setIsModalOpen(false);

      // Refresh list & select newly created request
      await loadRequests();
      setSelectedRequest(created);
      setActionMessage('New DSR submitted successfully.');
    } catch (err: any) {
      setFormError(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to submit DSR'
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Actions
  const handleVerify = async (requestId: string) => {
    setActionMessage(null);
    try {
      const updated = await dsrService.verifyRequest(requestId, {
        verified: true,
        verifiedBy: 'compliance-officer',
      });
      setRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, ...updated, status: updated.status || 'VERIFIED' } : r))
      );
      setSelectedRequest((prev) =>
        prev && prev.id === requestId ? { ...prev, ...updated, status: updated.status || 'VERIFIED' } : prev
      );
      setActionMessage(`Request ${requestId} identity verified.`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Verification failed');
    }
  };

  const handleExtend = async (requestId: string) => {
    setActionMessage(null);
    try {
      const updated = await dsrService.extendRequest(requestId, {
        days: 45,
        reason: 'Statutory 45-day CCPA extension requested due to complex retrieval',
      });
      setRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, ...updated, status: updated.status || 'EXTENDED' } : r))
      );
      setSelectedRequest((prev) =>
        prev && prev.id === requestId ? { ...prev, ...updated, status: updated.status || 'EXTENDED' } : prev
      );
      setActionMessage(`Statutory 45-day extension applied to ${requestId}.`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Extension failed');
    }
  };

  const handleFulfill = async (requestId: string) => {
    setActionMessage(null);
    try {
      const updated = await dsrService.fulfillRequest(requestId, {
        notes: 'Statutory compliance obligation completed.',
        fulfilledBy: 'privacy-officer',
      });
      setRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, ...updated, status: updated.status || 'FULFILLED' } : r))
      );
      setSelectedRequest((prev) =>
        prev && prev.id === requestId ? { ...prev, ...updated, status: updated.status || 'FULFILLED' } : prev
      );
      setActionMessage(`Request ${requestId} marked as fulfilled.`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Fulfillment failed');
    }
  };

  return (
    <div data-testid="dsr-portal" className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            US Data Subject Request (DSR) Portal
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            CCPA / CPRA Statutory Consumer Privacy Intake and Fulfillment Console
          </p>
        </div>
        <button
          type="button"
          data-testid="new-dsr-btn"
          onClick={() => {
            setFormError(null);
            setIsModalOpen(true);
          }}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm shadow-sm"
        >
          + New DSR Request
        </button>
      </div>

      {/* Notifications */}
      {actionMessage && (
        <div
          data-testid="action-notification"
          className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg flex justify-between items-center"
        >
          <span>{actionMessage}</span>
          <button
            type="button"
            onClick={() => setActionMessage(null)}
            className="text-emerald-700 hover:text-emerald-900 font-bold ml-4"
          >
            &times;
          </button>
        </div>
      )}

      {error && (
        <div
          data-testid="dsr-error"
          className="p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg flex justify-between items-center"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="text-red-700 hover:text-red-900 font-bold ml-4"
          >
            &times;
          </button>
        </div>
      )}

      {/* Status Filter Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200">
        {(
          [
            { id: 'ALL', label: 'All' },
            { id: 'PENDING', label: 'Pending' },
            { id: 'IN_PROGRESS', label: 'In Progress' },
            { id: 'FULFILLED', label: 'Fulfilled' },
          ] as const
        ).map((tab) => {
          const count = requests.filter((r) => {
            if (tab.id === 'ALL') return true;
            if (tab.id === 'PENDING') return r.status === 'PENDING';
            if (tab.id === 'IN_PROGRESS') {
              return (
                r.status === 'IN_PROGRESS' ||
                r.status === 'VERIFIED' ||
                r.status === 'EXTENDED'
              );
            }
            if (tab.id === 'FULFILLED') return r.status === 'FULFILLED';
            return true;
          }).length;

          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              data-testid={`tab-${tab.id.toLowerCase().replace('_', '-')}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition -mb-px ${
                isActive
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              <span
                className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                  isActive
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Main Content: Split Master-Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Request List Column */}
        <div className="lg:col-span-7">
          <div
            data-testid="dsr-list"
            className="bg-white border border-gray-200 rounded-lg shadow-sm divide-y divide-gray-200 overflow-hidden"
          >
            {loading ? (
              <div data-testid="dsr-loading" className="p-8 text-center text-gray-500">
                Loading statutory requests...
              </div>
            ) : filteredRequests.length === 0 ? (
              <div data-testid="dsr-empty" className="p-8 text-center text-gray-500">
                No requests found matching current filter.
              </div>
            ) : (
              filteredRequests.map((req) => {
                const isSelected = selectedRequest?.id === req.id;
                const overdue = isOverdue(req.deadline);
                const daysRemaining = calculateDaysRemaining(req.deadline);

                return (
                  <div
                    key={req.id}
                    data-testid="dsr-row"
                    data-request-id={req.id}
                    onClick={() => setSelectedRequest(req)}
                    className={`p-4 transition cursor-pointer hover:bg-slate-50 ${
                      isSelected ? 'bg-blue-50/70 border-l-4 border-l-blue-600' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900 text-sm">
                            {req.subjectName}
                          </span>
                          <span
                            className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                              req.type === 'DELETE'
                                ? 'bg-red-100 text-red-800'
                                : req.type === 'OPT_OUT'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-indigo-100 text-indigo-800'
                            }`}
                          >
                            {req.type}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {req.subjectEmail}
                        </p>
                      </div>

                      <div className="text-right">
                        <span
                          className={`inline-block px-2.5 py-0.5 text-xs font-medium rounded-full ${
                            req.status === 'FULFILLED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : req.status === 'PENDING'
                              ? 'bg-amber-100 text-amber-800'
                              : req.status === 'REJECTED'
                              ? 'bg-rose-100 text-rose-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {req.status}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 flex justify-between items-center text-xs text-gray-500">
                      <span>ID: {req.id}</span>
                      <span
                        data-testid="dsr-deadline"
                        className={`font-medium ${
                          overdue
                            ? 'text-red-600 font-bold'
                            : daysRemaining <= 7
                            ? 'text-amber-600 font-semibold'
                            : 'text-gray-600'
                        }`}
                      >
                        {formatDeadlineCountdown(req.deadline)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Per-DSR Detail View Column */}
        <div className="lg:col-span-5">
          <div
            data-testid="dsr-detail"
            className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-5"
          >
            {selectedRequest ? (
              <>
                <div className="border-b border-gray-200 pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="text-xs uppercase font-bold tracking-wider text-gray-400">
                        Request Detail
                      </span>
                      <h2 className="text-xl font-bold text-gray-900">
                        {selectedRequest.subjectName}
                      </h2>
                      <p className="text-sm text-gray-600">{selectedRequest.subjectEmail}</p>
                    </div>
                    <span
                      className={`px-3 py-1 text-xs font-semibold rounded-full ${
                        selectedRequest.status === 'FULFILLED'
                          ? 'bg-emerald-100 text-emerald-800'
                          : selectedRequest.status === 'PENDING'
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {selectedRequest.status}
                    </span>
                  </div>
                </div>

                {/* Metadata Grid */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-gray-50 rounded-md">
                    <span className="text-xs text-gray-500 block">Type</span>
                    <span className="font-semibold text-gray-800">{selectedRequest.type}</span>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-md">
                    <span className="text-xs text-gray-500 block">Request ID</span>
                    <span className="font-semibold text-gray-800 truncate block">
                      {selectedRequest.id}
                    </span>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-md">
                    <span className="text-xs text-gray-500 block">Submitted</span>
                    <span className="text-gray-800">
                      {new Date(selectedRequest.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-md">
                    <span className="text-xs text-gray-500 block">Deadline Countdown</span>
                    <span
                      data-testid="dsr-deadline"
                      className={`font-semibold block ${
                        isOverdue(selectedRequest.deadline)
                          ? 'text-red-600'
                          : 'text-gray-800'
                      }`}
                    >
                      {formatDeadlineCountdown(selectedRequest.deadline)}
                    </span>
                  </div>
                </div>

                {/* Request Details Content */}
                <div>
                  <h3 className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-1">
                    Details & Instructions
                  </h3>
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-gray-800 whitespace-pre-wrap">
                    {selectedRequest.details || 'No details provided.'}
                  </div>
                </div>

                {/* Action Buttons */}
                <div>
                  <h3 className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-2">
                    Compliance Actions
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid="dsr-verify-btn"
                      onClick={() => handleVerify(selectedRequest.id)}
                      disabled={
                        selectedRequest.status === 'VERIFIED' ||
                        selectedRequest.status === 'FULFILLED'
                      }
                      className="px-3.5 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Verify Identity
                    </button>

                    <button
                      type="button"
                      data-testid="dsr-extend-btn"
                      onClick={() => handleExtend(selectedRequest.id)}
                      disabled={
                        selectedRequest.status === 'EXTENDED' ||
                        selectedRequest.status === 'FULFILLED'
                      }
                      className="px-3.5 py-2 text-sm font-medium rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Extend (+45 Days)
                    </button>

                    <button
                      type="button"
                      data-testid="dsr-fulfill-btn"
                      onClick={() => handleFulfill(selectedRequest.id)}
                      disabled={selectedRequest.status === 'FULFILLED'}
                      className="px-3.5 py-2 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Fulfill Request
                    </button>
                  </div>
                </div>

                {/* Audit Trail */}
                {selectedRequest.auditTrail && selectedRequest.auditTrail.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold uppercase text-gray-500 tracking-wider mb-2">
                      Audit Trail
                    </h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                      {selectedRequest.auditTrail.map((entry, idx) => (
                        <div
                          key={idx}
                          data-testid="dsr-audit-entry"
                          className="p-2 bg-gray-50 rounded text-xs border border-gray-100"
                        >
                          <div className="flex justify-between text-gray-500">
                            <span className="font-semibold text-gray-700">{entry.action}</span>
                            <span>{new Date(entry.timestamp).toLocaleDateString()}</span>
                          </div>
                          <div className="text-gray-600 mt-0.5">
                            Actor: {entry.actor}
                            {entry.details ? ` — ${entry.details}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-8 text-center text-gray-400">
                Select a request from the list to view statutory details and compliance actions.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* New DSR Modal */}
      {isModalOpen && (
        <div
          data-testid="new-dsr-modal"
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-200 pb-3">
              <h2 className="text-lg font-bold text-gray-900">
                New Data Subject Request Intake
              </h2>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                &times;
              </button>
            </div>

            {formError && (
              <div className="p-3 bg-red-50 text-red-700 text-xs rounded border border-red-200">
                {formError}
              </div>
            )}

            <form onSubmit={handleCreateDSR} className="space-y-4">
              <div>
                <label
                  htmlFor="dsr-type-select"
                  className="block text-xs font-semibold text-gray-700 uppercase mb-1"
                >
                  Request Type
                </label>
                <select
                  id="dsr-type-select"
                  data-testid="dsr-type-select"
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as DSRType)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="ACCESS">ACCESS (Right to Know)</option>
                  <option value="DELETE">DELETE (Right to Delete)</option>
                  <option value="OPT_OUT">OPT_OUT (Do Not Sell/Share)</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="dsr-subject-name-input"
                  className="block text-xs font-semibold text-gray-700 uppercase mb-1"
                >
                  Subject Name
                </label>
                <input
                  id="dsr-subject-name-input"
                  data-testid="dsr-subject-name-input"
                  type="text"
                  required
                  placeholder="e.g. Jane Doe"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="dsr-subject-email-input"
                  className="block text-xs font-semibold text-gray-700 uppercase mb-1"
                >
                  Subject Email
                </label>
                <input
                  id="dsr-subject-email-input"
                  data-testid="dsr-subject-email-input"
                  type="email"
                  required
                  placeholder="e.g. jane.doe@example.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div>
                <label
                  htmlFor="dsr-details-input"
                  className="block text-xs font-semibold text-gray-700 uppercase mb-1"
                >
                  Details & Specific Requests
                </label>
                <textarea
                  id="dsr-details-input"
                  data-testid="dsr-details-input"
                  required
                  rows={4}
                  placeholder="Describe data categories, specific lease or tenancy scopes requested..."
                  value={newDetails}
                  onChange={(e) => setNewDetails(e.target.value)}
                  className="w-full p-2.5 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                <button
                  type="button"
                  data-testid="dsr-cancel-btn"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="dsr-submit-btn"
                  disabled={submitting}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DsrPortal;
